import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

interface EntranceApi {
  /** Scoped selector (gsap.context) — findet nur innerhalb der Section */
  q: gsap.utils.SelectorFunc
  /** Pausierte Timeline; der Builder hängt seine Choreografie hinein */
  tl: gsap.core.Timeline
}

/**
 * Section-spezifische Entrances: jede Section baut ihre eigene, auf den
 * Inhalt zugeschnittene Timeline (Bento-Welle, Preis-Zähler, Saiten, die
 * sich zeichnen …). Dieser Hook stellt nur das Gerüst:
 *
 *  - spielt einmalig beim ersten Sichtbarwerden (ScrollTrigger, once)
 *  - `from`-Tweens setzen ihre Startzustände sofort beim Aufbau; Elemente mit
 *    `.entrance` sind schon ab dem ersten Paint per CSS verborgen (`html.js`)
 *    — kein Aufblitzen des Endzustands, auch nicht bei Lazy-Hydration
 *  - bei `prefers-reduced-motion` wird nichts gebaut und nichts versteckt
 *  - gsap.context räumt beim Unmount alles ab
 */
export function useEntrance(
  container: Ref<HTMLElement | null>,
  build: (api: EntranceApi) => void,
  options: { trigger?: string, start?: string } = {}
) {
  let ctx: gsap.Context | undefined

  onMounted(async () => {
    await nextTick()
    if (!container.value) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    gsap.registerPlugin(ScrollTrigger)

    ctx = gsap.context((self) => {
      // gsap.Context typt den Selector nur als Function
      const q = self.selector as gsap.utils.SelectorFunc
      const tl = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } })
      // Die CSS-Vorab-Verstecke (`.js .entrance`, main.css) aufheben, BEVOR die
      // from-Tweens ihre Endwerte messen — synchron, also ohne Paint dazwischen
      // (die from-Tweens setzen ihre Startzustände sofort inline).
      container.value!.classList.add('is-entrance-ready')
      build({ q, tl })

      const trigger = (options.trigger && q(options.trigger)[0]) || container.value!
      ScrollTrigger.create({
        trigger,
        start: options.start ?? 'top 78%',
        once: true,
        onEnter: () => tl.play()
      })
    }, container.value)
  })

  onBeforeUnmount(() => ctx?.revert())
}
